import type React from 'react';

import { createTemplateExtractorSchema } from '@baseplate-dev/project-builder-lib';
import {
  useBlockUnsavedChangesNavigate,
  useDefinitionSchema,
  useProjectDefinition,
  useResettableForm,
} from '@baseplate-dev/project-builder-lib/web';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  CheckboxFieldController,
  FormActionBar,
  SectionList,
  SectionListSection,
  SectionListSectionContent,
  SectionListSectionHeader,
  SectionListSectionTitle,
  TextareaFieldController,
} from '@baseplate-dev/ui-components';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';

import { ENABLE_TEMPLATE_EXTRACTOR } from '#src/services/config.js';

export const Route = createFileRoute('/settings/template-extractor')({
  component: TemplateExtractorSettingsPage,
  beforeLoad: () => ({
    getTitle: () => 'Template Extractor',
  }),
});

/**
 * Settings page for template extractor configuration
 *
 * Allows users to control template metadata generation during the extraction process.
 */
function TemplateExtractorSettingsPage(): React.JSX.Element {
  const { definition, saveDefinitionWithFeedback } = useProjectDefinition();
  const defaultValues = definition.settings.templateExtractor ?? {
    writeMetadata: false,
    fileIdRegexWhitelist: '',
  };
  const templateExtractorSchema = useDefinitionSchema(
    createTemplateExtractorSchema,
  );
  const form = useResettableForm({
    resolver: zodResolver(templateExtractorSchema),
    defaultValues,
  });

  const { handleSubmit, control, reset } = form;

  const onSubmit = handleSubmit((data) =>
    saveDefinitionWithFeedback((draftConfig) => {
      draftConfig.settings.templateExtractor = data;
    }),
  );

  useBlockUnsavedChangesNavigate({ control, reset, onSubmit });

  if (!ENABLE_TEMPLATE_EXTRACTOR) {
    return (
      <Alert variant="error" className="mx-auto my-16 max-w-2xl">
        <AlertTitle>Template Extractor is disabled</AlertTitle>
        <AlertDescription>
          Template extractor is disabled in the environment. Please enable it
          with <code>VITE_ENABLE_TEMPLATE_EXTRACTOR=true</code> to use it.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <form
      className="relative h-full max-h-full pb-(--action-bar-height)"
      onSubmit={onSubmit}
    >
      <div className="flex h-full max-h-full flex-1 flex-col overflow-y-auto px-6">
        <div className="sticky top-0 border-b bg-background py-6">
          <h1>Template Extractor</h1>
        </div>
        <SectionList>
          <SectionListSection>
            <SectionListSectionHeader>
              <SectionListSectionTitle>Settings</SectionListSectionTitle>
            </SectionListSectionHeader>
            <SectionListSectionContent className="flex max-w-md flex-col gap-4">
              <CheckboxFieldController
                name="writeMetadata"
                label="Write Metadata"
                description="Write metadata to the project to enable template extraction"
                control={control}
              />
              <TextareaFieldController
                name="fileIdRegexWhitelist"
                label="File ID Regex Whitelist"
                description="A list of file IDs to include in the template extractor metadata for generators that have a manually assigned file IDs, e.g. files generated by generators that have multiple instances. The list is delimited by a newline and must be a valid regex."
                control={control}
              />
            </SectionListSectionContent>
          </SectionListSection>
        </SectionList>
      </div>
      <FormActionBar form={form} />
    </form>
  );
}
